import {MakeupLessonInfo} from '@/helpers/makeup/makeupLesson';
import {LeanDocument} from 'mongoose';
import {TimetableItem} from '@/interfaces/timetable/TimetableItem';
import {CourseDocument} from '@/documentTypes/course-manager/Course.document';
import {SubstitutionInquiryState} from '@/enums/substitution-manager/SubstitutionInquiryState';
import type {AgendaSpan} from '@/helpers/timetable2/agenda';
import type {BandKind, BandSource, BandTargets, BandToggles} from '@/helpers/timetable2/availabilityBands';

/**
 * Shared contract of the new calendar ("Etappe 2"). All timetable2 helpers and components
 * (toolbar, views, tiles, side panel, list/resource views) only talk to each other through these types.
 *
 * Conventions:
 * - Ids are plain strings (ObjectId.toString()), dates are local wall clock strings
 *   ('YYYY-MM-DD' for days, 'YYYY-MM-DD HH:mm' for points in time, same format as the scheduler).
 * - Weeks start on Monday (ISO week, Europe/Berlin); day index 0 = Monday … 6 = Sunday.
 */

export const DAY_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

/** Visible calendar view. 'teachers'/'rooms' show a weekly matrix (span 'week') or a day with one column per resource (span 'day') */
export type CalendarView = 'week' | 'day' | 'month' | 'list' | 'teachers' | 'rooms';

/** Time span of the resource views ('teachers' / 'rooms') */
export type ResourceSpan = 'week' | 'day';

/** How tiles are coloured: by subject (default) or per child (parent/student view, see childColors.ts) */
export type CalendarColorMode = 'subject' | 'child';

export type LeanCourse = LeanDocument<CourseDocument>;
export type CourseTimetableItem = TimetableItem<LeanCourse>;

/** Filters of the calendar. Empty arrays mean "no restriction". */
export interface CalendarFilters {
    institution: string[];
    teacher: string[];
    room: string[];
    subject: string[];
    student: string[];
    /** Parents are resolved to their students by the loader */
    parent: string[];
    /** Course customIds */
    course: string[];
    /** Show draft courses */
    drafts: boolean;
    /** Only lessons with a problem (vacant, missing log, conflict) */
    problems: boolean;
    color: CalendarColorMode;
}

/** Keys of CalendarFilters that restrict the requested lessons (scope guard: at least one for non-teachers) */
export type ScopingFilterKey = 'institution' | 'teacher' | 'room' | 'subject' | 'student' | 'parent' | 'course';
export const SCOPING_FILTER_KEYS: ScopingFilterKey[] = ['institution', 'teacher', 'room', 'subject', 'student', 'parent', 'course'];

/** Complete navigational state, serialised to / parsed from the URL by calendarQuery.ts */
export interface CalendarQueryState {
    view: CalendarView;
    span: ResourceSpan;
    /** Anchor day 'YYYY-MM-DD' */
    date: string;
    filters: CalendarFilters;
    /** Selected lesson key (LessonVM.key) or undefined */
    sel?: string;
}

/** Visible (and requested) range of the current view */
export interface CalendarRange {
    view: CalendarView;
    /** First visible day 'YYYY-MM-DD' (inclusive) */
    from: string;
    /** Last visible day 'YYYY-MM-DD' (inclusive) */
    to: string;
    /** All visible days in order */
    days: string[];
    /** Start of `from` (local time) */
    fromDate: Date;
    /** End of `to` (23:59:59.999 local time) */
    toDate: Date;
    /** ISO week and ISO week year of the anchor date */
    isoWeek: number;
    isoWeekYear: number;
    /** Main label, e.g. "KW 41" / "Oktober 2026" / "Dienstag, 6. Oktober" */
    label: string;
    /** Secondary label, e.g. "5.–11. Oktober 2026" */
    sublabel: string;
}

/** State of the course logs of one past occurrence */
export type LogState =
    /** Lesson has not ended yet */
    'upcoming'
    /** Canceled / holiday / vacant: no logs expected */
    | 'notExpected'
    /** No participating students */
    | 'noStudents'
    /** Every participating student has a log */
    | 'complete'
    /** Some logs exist */
    | 'partial'
    /** Ended, no log at all */
    | 'missing';

/**
 * Derived status of one occurrence; precedence moved (ghost of a moved-away lesson) > holiday > canceled > vacant
 * > substituted > draft > regular
 */
export type LessonStatusKind = 'moved' | 'holiday' | 'canceled' | 'vacant' | 'substituted' | 'draft' | 'regular';

/** Tones of modern.css (.tone-*) */
export type Tone = 'good' | 'warn' | 'info' | 'teal' | 'muted' | 'bad' | 'purple';

export interface LessonStatusInfo {
    kind: LessonStatusKind;
    /** Short German text for chips, e.g. "Ausfall", "Vertretung gesucht" */
    text: string;
    /** mdi icon */
    icon: string;
    tone: Tone;
    /** Counts as a problem for the "Nur Probleme" filter */
    problem: boolean;
}

/** Substitution inquiry data relevant for the calendar (loaded in one batch per range) */
export interface InquiryRef {
    id: string;
    state: SubstitutionInquiryState;
    /** A cancellation (Ausfall) was configured – sent or scheduled */
    cancellationConfigured: boolean;
    /**
     * The cancellation really happened (started / sent or inquiry cancelled). Only then the lesson is "Fällt aus";
     * before (planned automatic cancellation, stopped, saved for the review) it is "Ausfall gemeldet: …" (user 12.10.2026)
     */
    cancellationSent?: boolean;
    cancellationSendAt?: string;
    /** Manual cancellation stored with "Prüfen vor dem Senden", messages not sent yet */
    cancellationPending?: boolean;
    /** Free text / reason of the cancellation (optional backend field, Etappe 2 B4) */
    cancellationReasonText?: string;
    /** "Automatische Absage stoppen" was used (not sent, the office decides) */
    autoCancelStopped?: boolean;
}

export type ConflictKind = 'teacher' | 'room' | 'student';

/** Overlap of this lesson with another lesson of the loaded range */
export interface ConflictRef {
    kind: ConflictKind;
    /** LessonVM.key of the other lesson */
    otherKey: string;
    resourceId: string;
    resourceName: string;
    /** German text, e.g. "Raum Musiksaal doppelt belegt (KL203)" */
    text: string;
}

/** View model of one course occurrence ("Stunde") */
export interface LessonVM {
    /** Stable occurrence key `${customId}|${start}` (same as the Today view) */
    key: string;
    /** Original scheduler item: legacy dialogs (TimetableItem<Course>) get exactly this object */
    item: CourseTimetableItem;
    /** _id of the course segment */
    courseId: string;
    customId: string;
    /** 'YYYY-MM-DD HH:mm' */
    start: string;
    end: string;
    /** 'YYYY-MM-DD' */
    day: string;
    /** 0 = Monday … 6 = Sunday */
    dayIndex: number;
    /** Minutes since midnight (wall clock) */
    startMinutes: number;
    endMinutes: number;
    startMs: number;
    endMs: number;
    subjectId: string;
    subjectName: string;
    /** Subject colour (vuetify colour name or hex) */
    color: string;
    teacherId: string;
    teacherName: string;
    /** Regular teacher (differs from teacher while substituted) */
    owningTeacherId: string;
    owningTeacherName: string;
    roomId: string;
    roomName: string;
    institutionId: string;
    institutionName: string;
    /** Students that take part in this occurrence (participation not ended) */
    studentIds: string[];
    /** Same order as studentIds ('' if the student is not populated) */
    studentNames: string[];
    participantCount: number;
    weekInterval: number;
    isDraft: boolean;
    isOpenForRegistration: boolean;
    isPast: boolean;
    isRunning: boolean;
    status: LessonStatusInfo;
    inquiry?: InquiryRef;
    logState: LogState;
    loggedCount: number;
    expectedLogCount: number;
    conflicts: ConflictRef[];
    /** Problem for the "Nur Probleme" filter: vacant, missing/partial logs or conflicts */
    hasProblem: boolean;
    /** "Stunde einmalig verschieben": 'moved' = the moved lesson, 'origin' = ghost at the original date (never a lesson) */
    moveRole?: 'moved' | 'origin';
    /** "Verschoben vom 12.10." (moved) / "Verschoben auf 19.10., 15:00" (origin) */
    moveText?: string;
    /** Moved lesson across a teacher change without decision ("Lehrkraft ungeklärt"): billed to nobody until decided */
    teacherDecisionPending?: boolean;
    /** Make-up course ("Nachholtermin"): "Ersatz für K1 (Mo 05.10.)" + primary cancelled lesson */
    makeup?: MakeupLessonInfo;
}

/** Holiday shown as all-day band */
export interface HolidayVM {
    key: string;
    name: string;
    /** 'YYYY-MM-DD' inclusive */
    startDay: string;
    endDay: string;
    item: TimetableItem<any>;
}

/** Handout event (only loaded with teacher/institution filter, shown via the legacy TimetableHandoutEvent) */
export interface HandoutVM {
    key: string;
    start: string;
    end: string;
    day: string;
    item: TimetableItem<any>;
}

/** Selection shown in the side panel (CalLessonPanel) */
export interface PanelSelection {
    /** LessonVM.key */
    key: string;
}

export interface LegendEntry {
    key: string;
    text: string;
    icon: string;
    tone: Tone;
    /** Longer explanation (tooltip) */
    description?: string;
}

/** Props of the legacy Timetable.vue, accepted unchanged by TimetableV2 in embedded mode */
export interface EmbeddedCalendarProps {
    customId?: string[];
    teacher?: unknown[];
    institution?: unknown[];
    student?: unknown[];
    requireIsOpenForRegistration?: boolean;
    hideDrafts?: boolean;
}

export interface CalendarState {
    view: CalendarView;
    span: ResourceSpan;
    date: string;
    filters: CalendarFilters;
    /** Filters fixed by the embedding (profile tabs); the filter bar shows them read-only */
    lockedFilters: Partial<CalendarFilters>;
    embedded: boolean;
    requireIsOpenForRegistration: boolean;
    range: CalendarRange;
    loading: boolean;
    /** German error text of the last failed load */
    error: string;
    /** No scoping filter set (non-teacher, standalone): nothing is loaded */
    needsScope: boolean;
    lessons: LessonVM[];
    holidays: HolidayVM[];
    handouts: HandoutVM[];
    selection: PanelSelection | null;
    /** Drag & drop (move/resize/create) enabled */
    dragEnabled: boolean;
    /** Increases with every successful load (watchers / scroll restore) */
    revision: number;
    /** List view: span and additional weeks ("Ältere laden" / "Mehr laden"), reset on navigation */
    listSpan: AgendaSpan;
    listExtraBefore: number;
    listExtraAfter: number;
    /** Availability bands of the week / day view (availabilityBands.ts); not used by the list-only agenda store */
    bands?: AvailabilityBandState;
}

export interface AvailabilityBandState {
    /** Which band kinds are switched on (legend toggles, remembered per browser) */
    toggles: BandToggles;
    /** Exactly one readable teacher / Standort in the effective filters */
    targets: BandTargets;
    /** Band kinds the user may switch on for the current targets */
    kinds: BandKind[];
    source: BandSource;
    loading: boolean;
    error: string;
}

/** Per-instance calendar store, provided by TimetableV2 under CALENDAR_STORE_KEY (inject: ['calendarStore']) */
export interface CalendarStore {
    readonly state: CalendarState;
    setView(view: CalendarView, span?: ResourceSpan): void;
    setDate(date: string): void;
    /** Moves by one view unit (-1 / +1) */
    shift(direction: -1 | 1): void;
    today(): void;
    setFilters(patch: Partial<CalendarFilters>): void;
    select(key: string | null): void;
    lessonByKey(key: string): LessonVM | undefined;
    /** Reloads the current range; force bypasses the cache */
    reload(force?: boolean): Promise<void>;
    /** Lessons after the "Nur Probleme" filter (what the views render) */
    visibleLessons(): LessonVM[];
    /** List view range: span + extra weeks before/after (one scheduler request per range) */
    setListRange(span: AgendaSpan, extraBefore?: number, extraAfter?: number): void;
    /** Switches one kind of availability band on / off (loads the availabilities on demand) */
    setBandToggle?(kind: BandKind, value: boolean): void;
}

export const CALENDAR_STORE_KEY = 'calendarStore';
